import { CurrencyEnum, Popover, Tooltip, copyTextToClipboard } from '@systemeio/ui'
import { AffiliateDashboardStatisticInterface } from 'modules/affiliate-dashboard/types/affiliate-dashboard-statistic-interface'
import { AffiliateStatisticCommonInterface } from 'modules/affiliate-program/affiliate/types/affiliate-interface'
import {
  getAffiliateSum,
  getAffiliateTotalSum,
} from 'modules/affiliate-program/utils/get-affiliate-sum'
import React, { useEffect, useState } from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import CopyIcon from 'shared/icons/copy-icon'
import QuestionMarkIcon from 'shared/icons/question-mark-icon'

interface AffiliateStatisticProps {
  affiliateStatistic?: AffiliateStatisticCommonInterface
  selectedCurrency?: CurrencyEnum
  isDashboard?: boolean
}

const AffiliateStatistic = ({
  affiliateStatistic,
  selectedCurrency,
  isDashboard = false,
}: AffiliateStatisticProps) => {
  const { t } = useLocoTranslation()
  const { user } = useUser()

  const [mainCurrency, setMainCurrency] = useState<CurrencyEnum | undefined>()

  useEffect(() => {
    if (!affiliateStatistic) return

    const allCurrencies = Object.keys(affiliateStatistic.total) as CurrencyEnum[]
    if (selectedCurrency) {
      setMainCurrency(undefined)
    } else {
      setMainCurrency(
        allCurrencies[
          Object.values(affiliateStatistic.total).reduce(
            (iMax, x, i, arr) => (x > arr[iMax] ? i : iMax),
            0,
          )
        ],
      )
    }
  }, [selectedCurrency, affiliateStatistic])

  return (
    <div className={'flex flex-col gap-5'}>
      <div className={'flex flex-col w-full bg-white rounded-lg overflow-x-hidden'}>
        <span className={'text-darkblue font-bold px-6 py-4 bg-blue-100 text-[17px]'}>
          {t('dashboard.affiliate.earnings')}
        </span>
        <div className={'px-6 py-4 flex flex-col'}>
          <StatisticWrapper title={t('dashboard.affiliate.today')} isFirst>
            <StatisticContent affiliateStatistic={affiliateStatistic}>
              {affiliateStatistic =>
                selectedCurrency
                  ? getAffiliateSum(
                      affiliateStatistic.today,
                      selectedCurrency,
                      user?.dashboardLocale,
                    )
                  : getAffiliateTotalSum(
                      affiliateStatistic.today,
                      t,
                      mainCurrency,
                      user?.dashboardLocale,
                    )
              }
            </StatisticContent>
          </StatisticWrapper>
          <StatisticWrapper title={t('dashboard.affiliate.last_7_days')}>
            <StatisticContent affiliateStatistic={affiliateStatistic}>
              {affiliateStatistic =>
                selectedCurrency
                  ? getAffiliateSum(
                      affiliateStatistic.last7Days,
                      selectedCurrency,
                      user?.dashboardLocale,
                    )
                  : getAffiliateTotalSum(
                      affiliateStatistic.last7Days,
                      t,
                      mainCurrency,
                      user?.dashboardLocale,
                    )
              }
            </StatisticContent>
          </StatisticWrapper>
          <StatisticWrapper title={t('dashboard.affiliate.last_30_days')}>
            <StatisticContent affiliateStatistic={affiliateStatistic}>
              {affiliateStatistic =>
                selectedCurrency
                  ? getAffiliateSum(
                      affiliateStatistic.last30Days.sums,
                      selectedCurrency,
                      user?.dashboardLocale,
                    )
                  : getAffiliateTotalSum(
                      affiliateStatistic.last30Days.sums,
                      t,
                      mainCurrency,
                      user?.dashboardLocale,
                    )
              }
            </StatisticContent>
          </StatisticWrapper>
          <StatisticWrapper title={t('dashboard.affiliate.paid')}>
            <StatisticContent affiliateStatistic={affiliateStatistic}>
              {affiliateStatistic =>
                selectedCurrency
                  ? getAffiliateSum(
                      affiliateStatistic.paidSums,
                      selectedCurrency,
                      user?.dashboardLocale,
                    )
                  : getAffiliateTotalSum(
                      affiliateStatistic.paidSums,
                      t,
                      mainCurrency,
                      user?.dashboardLocale,
                    )
              }
            </StatisticContent>
          </StatisticWrapper>
          <StatisticWrapper title={t('dashboard.affiliate.pending')}>
            <StatisticContent affiliateStatistic={affiliateStatistic}>
              {affiliateStatistic =>
                selectedCurrency
                  ? getAffiliateSum(
                      affiliateStatistic.pendingSums,
                      selectedCurrency,
                      user?.dashboardLocale,
                    )
                  : getAffiliateTotalSum(
                      affiliateStatistic.pendingSums,
                      t,
                      mainCurrency,
                      user?.dashboardLocale,
                    )
              }
            </StatisticContent>
          </StatisticWrapper>
          <StatisticWrapper isLast title={t('dashboard.affiliate.lifetime_earnings')}>
            <StatisticContent affiliateStatistic={affiliateStatistic}>
              {affiliateStatistic =>
                selectedCurrency
                  ? getAffiliateSum(
                      affiliateStatistic.total,
                      selectedCurrency,
                      user?.dashboardLocale,
                    )
                  : getAffiliateTotalSum(
                      affiliateStatistic.total,
                      t,
                      mainCurrency,
                      user?.dashboardLocale,
                    )
              }
            </StatisticContent>
          </StatisticWrapper>
        </div>
      </div>
      <div className={'flex flex-col gap-3 w-full bg-white rounded-lg overflow-x-hidden px-6 py-4'}>
        {isDashboardStatistic(isDashboard, affiliateStatistic) ? (
          <div className={'flex gap-1.5 justify-between w-full'}>
            <span className={'text-darkblue whitespace-nowrap'}>
              {t('dashboard.affiliate_dashboard.your_affiliate_id')}
            </span>
            <div className={'flex gap-1.5 w-full truncate justify-end'}>
              <StatisticContent affiliateStatistic={affiliateStatistic}>
                {affiliateStatistic => affiliateStatistic.user.affiliateAccount.affiliateId}
              </StatisticContent>
              {affiliateStatistic && (
                <Tooltip mode="hover" label={t('global.copy')}>
                  <button
                    onClick={async () => {
                      await copyTextToClipboard(
                        affiliateStatistic.user.affiliateAccount.affiliateId,
                        t('global.copy_clipboard'),
                      )
                    }}
                    className="outline-none group"
                  >
                    <CopyIcon className="cursor-pointer group-focus-visible:fill-blue hover:fill-blue w-[16px] h-[16px]" />
                  </button>
                </Tooltip>
              )}
              <Popover
                label={t('dashboard.affiliate_dashboard.affiliate_id_tooltip')}
                popoverClassName={'w-[250px] sm:w-[450px]'}
              >
                {show => (
                  <QuestionMarkIcon
                    className={`${
                      show ? 'fill-blue' : 'fill-gray-100'
                    } group-focus-visible:fill-blue w-[16px] h-[16px]`}
                  />
                )}
              </Popover>
            </div>
          </div>
        ) : (
          <div className={'flex gap-1.5 justify-between w-full'}>
            <span className={'text-darkblue whitespace-nowrap'}>
              {t('dashboard.affiliate.affiliate_id')}
            </span>
            <StatisticContent affiliateStatistic={affiliateStatistic}>
              {affiliateStatistic => affiliateStatistic.contact.affiliateAccount.affiliateId}
            </StatisticContent>
          </div>
        )}
        <div className={'flex gap-1.5 justify-between w-full'}>
          <span className={'text-darkblue'}>{t('dashboard.affiliate.total_leads')}</span>
          <StatisticContent affiliateStatistic={affiliateStatistic}>
            {affiliateStatistic => affiliateStatistic.totalLeads}
          </StatisticContent>
        </div>
      </div>
    </div>
  )
}

const StatisticWrapper = ({
  title,
  isLast,
  isFirst,
  children,
}: {
  title: string
  isLast?: boolean
  isFirst?: boolean
  children: JSX.Element
}) => (
  <div
    className={`flex gap-1.5 justify-between ${isFirst && 'pt-0'} ${
      isLast ? 'pt-5' : 'py-5 border-b border-b-gray/30'
    } items-center`}
  >
    <span className={'text-darkblue'}>{title}</span>
    {children}
  </div>
)

const isDashboardStatistic = (
  isDashboard: boolean,
  stats?: AffiliateStatisticCommonInterface,
): stats is AffiliateDashboardStatisticInterface => isDashboard

const StatisticContent = <T,>({
  affiliateStatistic,
  children,
}: {
  affiliateStatistic?: T
  children: (affiliateStatistic: T) => JSX.Element | string | number
}) =>
  affiliateStatistic ? (
    <span className={'text-darkblue font-bold truncate'}>{children(affiliateStatistic)}</span>
  ) : (
    <span className={'animate-pulse bg-gray-600 rounded-lg text-transparent px-5'}>.</span>
  )

export default AffiliateStatistic
